import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
var _class, _class2, _class3;
/*
 *                     NOTICE
 *            COPYRIGHT 2023 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */
import { Expression } from "abreact/expression/Expression";
import { GuidedExpressionEditor } from "abreact/expression/ui/editor/GuidedExpressionEditor";
import { BaseGuidedExpressionEditor } from "abreact/expression/ui/editor/impl/BaseGuidedExpressionEditor";
import { GuidedRowEditor } from "abreact/expression/ui/editor/impl/condition/GuidedRowEditor";
import { OperatorOperandValueImpl } from "abreact/expression/ui/editor/impl/model/impl/row/operandValue/OperatorOperandValueImpl";
import { RowBasedRowModelImpl } from "abreact/expression/ui/editor/impl/model/impl/row/RowBasedRowModelImpl";
import { AbBooleanLogicTree } from "abreact/incubator/ui/abBooleanLogicTree/AbBooleanLogicTree";
import { SelectionMachineAction } from "abreact/incubator/ui/abBooleanLogicTree/model/AbBooleanLogicTreeSelectionMachine";
import { Assert } from "abweb/utils/Assert";
import { AbMaybePromise } from "abweb/utils/async/AbMaybePromise";
import { TypeUtils } from "abweb/utils/TypeUtils";
import * as Immutable from "immutable";
import { computed, makeObservable } from "mobx";
import { observer } from "mobx-react";
import * as React from "react";
// import ValidatedExpOperator = Expression.Domain.ValidatedExpOperator;
// import JoinOperandValue = GuidedExpressionEditor.AbstractModel.AbstractRowModel.JoinOperandValue;
// import OperatorOperandValue = GuidedExpressionEditor.AbstractModel.AbstractRowModel.OperatorOperandValue;
// import AbstractRowBasedModel = GuidedExpressionEditor.AbstractRowBasedModel;
let GuidedRowBasedExpressionEditor = observer(_class = (_class2 = class GuidedRowBasedExpressionEditor extends BaseGuidedExpressionEditor {
  constructor(_props) {
    super(_props);
    _defineProperty(this, "GuidedRowEditorWrapper", props => {
      const {
        conditionKey: rowKey
      } = props;
      const baseProps = {
        rowModel: this._model.getRowModel(rowKey),
        operatorRoleID: this._model.operatorRoleID,
        liveViewSliceModel: this.props.liveViewSliceModel,
        expressionDomainModel: this.props.expressionDomainModel,
        componentFactory: this.props.componentFactory,
        dataTestId: this.dataTestIdGenerator.generateID(rowKey),
        disabled: props.disabled ?? false,
        expTemplateConfiguration: this.props.propsExtension.expTemplateConfiguration,
        debug: this.props.debug,
        generateId: this._model.generateId,
        rowPositionInGroup: this._model.getRowPositionInGroup(rowKey),
        rowKey: rowKey,
        conditionPrefixFunction: this.props.propsExtension.conditionPrefixFunction
      };
      if (this.props.isReadonly || props.readonly || props.disabled) {
        return /*#__PURE__*/React.createElement(GuidedRowEditor, _extends({
          key: rowKey,
          isReadonly: true
        }, baseProps));
      } else {
        return /*#__PURE__*/React.createElement(GuidedRowEditor, _extends({
          key: rowKey,
          isReadonly: false
        }, baseProps, {
          customizationFunctions: this.props.customizationFunctions,
          applyRowModel: this.applyRowModel
        }));
      }
    });
    this.canDeleteRow = this.canDeleteRow.bind(this);
    this.canGroupWithNext = this.canGroupWithNext.bind(this);
    this.applyTreeContent = this.applyTreeContent.bind(this);
    this.onSelectionAction = this.onSelectionAction.bind(this);
    this.applyRowModel = this.applyRowModel.bind(this);
    makeObservable(this);
  }
  get guidedExpressionEditor() {
    return /*#__PURE__*/React.createElement(AbBooleanLogicTree, {
      key: this.props.expressionDomainModel.id,
      editorContent: this._model.booleanLogicTreeContent.treeContent,
      readonly: this.props.isReadonly,
      onChange: this.applyTreeContent,
      onSelectionAction: this.onSelectionAction,
      onBeforeUndoableChange: TypeUtils.emptyFunction,
      onAfterUndoableChange: TypeUtils.emptyFunction,
      conditionEvaluator: this.conditionEvaluator,
      customConditionComponent: this.GuidedRowEditorWrapper,
      keyGenerator: this._model.generateId,
      dataTestIdGenerator: this.dataTestIdGenerator,
      canDeleteRow: !this.props.isReadonly ? this.canDeleteRow : undefined,
      canGroupWithNext: !this.props.isReadonly ? this.canGroupWithNext : undefined,
      serverSupportsDisabledConditionSection: !this.props.isReadonly && (this.props.propsExtension.allowDisabling ?? false),
      supportsSubgroups: this._model.supportsSubgroups,
      rowLanguageSource: this._model.rowLanguageSource
    });
  }
  applyRowModel(rowKey, rowModelMaybePromise, context) {
    Assert.assert(!this.props.isReadonly);
    const modelMaybePromise = AbMaybePromise.map(rowModelMaybePromise, rowModel => {
      let model = this._model;
      if (rowModel !== model.getRowModel(rowKey)) {
        rowModel = this.injectPlaceholderOperatorOperandValueIfApplicable(rowModel);
        let {
          booleanLogicTreeContent
        } = model;
        booleanLogicTreeContent = booleanLogicTreeContent.withItemContent(rowKey, rowModel);
        model = model.withBooleanLogicTreeContent(booleanLogicTreeContent);
      }
      return model;
    });
    this.applyModel(modelMaybePromise, context);
  }
  injectPlaceholderOperatorOperandValueIfApplicable(rowModel) {
    const {
      placeholderExpOperator
    } = this;
    if (placeholderExpOperator != null && rowModel.operatorOperandValue == null) {
      const {
        expressionDomainModel
      } = this.props;
      const operatorOperandValue = new OperatorOperandValueImpl({
        id: rowModel.id,
        ...GuidedExpressionEditor.AbstractModel.AbstractRowModel.OperatorOperandValue.partialStateFromExpOperator(placeholderExpOperator, expressionDomainModel)
      });
      rowModel = rowModel.withOperatorOperandValue(operatorOperandValue);
    }
    return rowModel;
  }
  get placeholderExpOperator() {
    const {
      operatorRoleID
    } = this._model;
    return this.props.expressionDomainModel.findPlaceholderExpOperatorForExpOperatorRole(operatorRoleID);
  }
  get conditionEvaluator() {
    const {
      liveViewSliceModel
    } = this.props;
    if (liveViewSliceModel?.hasResults) {
      return ({
        itemKey
      }) => {
        const model = this._model;
        const operatorOperandValue = model.booleanLogicTreeContent.getItemContent(itemKey)?.operatorOperandValue;
        if (operatorOperandValue != null) {
          return liveViewSliceModel.findEvaluationResult(operatorOperandValue.id);
        }
      };
    }
  }
  get dataTestIdGenerator() {
    return {
      generateID: (rowKey, suffix) => {
        let dataTestId = this._model.generateFullyQualifiedDataTestId(rowKey);
        if (suffix) {
          dataTestId += `-${suffix}`;
        }
        if (this.props.dataTestIdPrefix) {
          dataTestId = `${this.props.dataTestIdPrefix}-${dataTestId}`;
        }
        return dataTestId;
      }
    };
  }
  canDeleteRow(level, isFirstItem, isLastItem) {
    Assert.assert(!this.props.isReadonly);
    return this.props.propsExtension.canDeleteRow?.(level, isFirstItem, isLastItem) ?? true;
  }
  canGroupWithNext(level, isFirstItem, isLastItem) {
    Assert.assert(!this.props.isReadonly);
    return this.props.propsExtension.canGroupWithNext?.(level, isFirstItem, isLastItem) ?? !isLastItem;
  }
  applyTreeContent(treeContent, additionalInfo) {
    Assert.assert(!this.props.isReadonly);
    const model = this.integrateModelChangesByKey(this._model, treeContent, additionalInfo);
    this.applyModel(model);
  }
  integrateModelChangesByKey(model, treeContent, additionalInfo) {
    Assert.assert(!this.props.isReadonly);
    const joinOperandValuesByGroupingContentKey = new Map();
    const rowModelsByRowKey = new Map();
    this.populateJoinModelsAndRowModelsFromGroupingContent(model, treeContent.nestingState, additionalInfo, joinOperandValuesByGroupingContentKey, rowModelsByRowKey);
    model = model.withJoinOperandValuesByGroupingContentKey(Immutable.Map(joinOperandValuesByGroupingContentKey));
    let {
      booleanLogicTreeContent
    } = model;
    booleanLogicTreeContent = booleanLogicTreeContent.withTreeContent(treeContent);
    booleanLogicTreeContent = booleanLogicTreeContent.withItemTable(Immutable.Map(rowModelsByRowKey));
    model = model.withBooleanLogicTreeContent(booleanLogicTreeContent);
    return model;
  }
  populateJoinModelsAndRowModelsFromGroupingContent(model, groupingContent, additionalInfo, joinOperandValuesByGroupingContentKey, rowModelsByRowKey) {
    model.applyGroupingContent(groupingContent, joinOperandValuesByGroupingContentKey, this.props.expressionDomainModel);
    for (const child of groupingContent.sequence.toArray()) {
      if (child.cardinality === "GROUP") {
        this.populateJoinModelsAndRowModelsFromGroupingContent(model, child, additionalInfo, joinOperandValuesByGroupingContentKey, rowModelsByRowKey);
      } else {
        const rowKey = child.key;
        const rowModel = this.resolveRowModel(model, rowKey, additionalInfo);
        rowModelsByRowKey.set(rowKey, rowModel);
      }
    }
  }
  resolveRowModel(model, rowKey, additionalInfo) {
    let rowModel = model.findRowModel(rowKey);
    if (rowModel == null) {
      Assert.assert(additionalInfo?.newRowKeys?.includes(rowKey) === true);
      /**
       * It is worth noting that, given the atomicity of action-menu-caused updates, if
       * {@link additionalInfo.duplicatedRowKey} is defined, there must be only one {@link rowKey} for which
       * {@link rowModel} is undefined, namely, the key that will contain the duplicated model. (For example,
       * both conventionally adding a new row and duplicating a row would require two separate updates).
       *
       * As such, although this logic exists in a recursive function, it is guaranteed to be called at most once.
       */
      if (additionalInfo.duplicatedRowKey != null) {
        /**
         * {@link rowKey} represents a new row with the contents of the row given by
         * {@link additionalInfo#duplicatedRowKey}. Since the duplicated row still exists in the tree, this new
         * duplicate row requires id regeneration.
         */
        Assert.assert(additionalInfo.newRowKeys.length === 1);
        rowModel = model.getRowModel(additionalInfo.duplicatedRowKey);
        rowModel = this.recursivelyRegenerateIds(model, rowKey, rowModel);
      } else {
        rowModel = RowBasedRowModelImpl.create(rowKey);
      }
    } else if (additionalInfo?.newRowKeys?.includes(rowKey)) {
      /**
       * {@link rowKey} represents a new row with the contents of a pasted or dropped row.
       *
       * A new row created during an internal paste requires id regeneration because the copied row still exists in the
       * tree. Conversely, a new row created during an internal drop does not require id regeneration, because the
       * dragged row is inherently no longer in the tree. (An internal drag and drop interaction is implemented as a
       * cut and a paste).
       *
       * A new row created during an external paste or drop always requires id regeneration, because its ids were
       * generated by a different model's id generation function and could theoretically overlap with ids in
       * {@link model}.
       *
       * Because internal and external drops are indistinguishable and id regeneration is inexpensive, we perform it in
       * both cases even though it is only required in the latter.
       */
      rowModel = this.recursivelyRegenerateIds(model, rowKey, rowModel);
    }
    return this.injectPlaceholderOperatorOperandValueIfApplicable(rowModel);
  }

  /**
   * Regenerates the id of {@link rowModel} as well as those of any descendants to ensure all are unique in the context
   * of {@link model}. Note that this function's invocation is predicated on / guarded by {@link rowKey} having been
   * newly created by the model, so it is guaranteed to be unique.
   */
  recursivelyRegenerateIds(model, rowKey, rowModel) {
    return rowModel.withId(rowKey).transform(rootOperandValue => {
      return OperatorOperandValueImpl.isInstance(rootOperandValue) ? rootOperandValue : rootOperandValue.transformRecursive(operandValue => operandValue.withId(model.generateId()));
    });
  }
  onSelectionAction(additionalInfo) {
    const {
      selectionAction
    } = additionalInfo;
    Assert.assert(selectionAction != null);
    const selectionMachine = selectionAction.selectionMachine;
    if (this.props.isReadonly) {
      Assert.assert(SelectionMachineAction.isReadonly(selectionAction.actionType));
    }
    const serialize = async booleanLogicTreeContent => {
      const model = this._model.withBooleanLogicTreeContent(booleanLogicTreeContent);
      const expExpressionDefinition = await this.props.modelMediator.export(model);
      return Expression.Definition.serialize(expExpressionDefinition);
    };
    const deserialize = async expExpressionDefinitionJson => {
      const expExpressionDefinition = Expression.Definition.parse(expExpressionDefinitionJson);
      const model = await this.props.modelMediator.import(expExpressionDefinition);
      return model.booleanLogicTreeContent;
    };
    const executeSelectionActionPromise = selectionMachine.executeAbBooleanLogicTreeSelectionAction(additionalInfo, this._model.booleanLogicTreeContent, serialize, deserialize);
    if (SelectionMachineAction.isReadonly(selectionAction.actionType)) {
      executeSelectionActionPromise.then(updatedBooleanLogicTreeContent => {
        Assert.assert(updatedBooleanLogicTreeContent == null);
      });
    } else {
      Assert.assert(!this.props.isReadonly);
      const modelPromise = executeSelectionActionPromise.then(updatedBooleanLogicTreeContent => {
        Assert.assert(updatedBooleanLogicTreeContent != null);
        let model = this._model;
        model = model.withBooleanLogicTreeContent(updatedBooleanLogicTreeContent);
        return this.integrateModelChangesByKey(model, model.booleanLogicTreeContent.treeContent, additionalInfo);
      });
      this.applyModel(modelPromise);
    }
  }
}, _applyDecoratedDescriptor(_class2.prototype, "guidedExpressionEditor", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "guidedExpressionEditor"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "placeholderExpOperator", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "placeholderExpOperator"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "conditionEvaluator", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "conditionEvaluator"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "dataTestIdGenerator", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "dataTestIdGenerator"), _class2.prototype), _class2)) || _class;
let GuidedRowBasedExpressionEditorWrapper = observer(_class3 = class GuidedRowBasedExpressionEditorWrapper extends React.Component {
  render() {
    const baseProps = {
      className: this.props.className,
      expExpressionDefinition: this.props.expExpressionDefinition,
      modelMediator: this.props.modelMediator,
      componentFactory: this.props.componentFactory,
      expressionDomainModel: this.props.expressionDomainModel,
      liveViewSliceModel: this.props.liveViewSliceModel,
      debug: this.props.debug,
      dataTestIdPrefix: this.props.dataTestIdPrefix,
      propsExtension: {
        expTemplateConfiguration: this.props.expTemplateConfiguration,
        conditionPrefixFunction: this.props.conditionPrefixFunction
      }
    };
    if (this.props.isReadonly) {
      return /*#__PURE__*/React.createElement(GuidedRowBasedExpressionEditor, _extends({}, baseProps, {
        isReadonly: true
      }));
    } else {
      return /*#__PURE__*/React.createElement(GuidedRowBasedExpressionEditor, _extends({}, baseProps, {
        isReadonly: false,
        customizationFunctions: this.props.customizationFunctions,
        changeTracker: this.props.changeTracker,
        onChange: this.props.onChange,
        propsExtension: {
          ...baseProps.propsExtension,
          allowDisabling: this.props.allowDisabling,
          canDeleteRow: this.props.canDeleteRow,
          canGroupWithNext: this.props.canGroupWithNext
        }
      }));
    }
  }
}) || _class3;
export { GuidedRowBasedExpressionEditorWrapper as GuidedRowBasedExpressionEditor };
//# sourceMappingURL=GuidedRowBasedExpressionEditor.js.map
